import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Text,
} from '@chakra-ui/react'
import { Formik } from 'formik'
import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../ui-kit'
import useToast from '../../hooks/useToast'
import type { Webhook } from '../../interfaces/WebhooksGetResponse'
import { UserContext } from '../../providers'

interface Props {
  isOpen: boolean
  webhook?: Webhook
  reloadWebhooks(): void
  onClose(): void
  onWebhookDelete(webhookId: string): void
}

const WebhookDeleteModal = ({
  isOpen,
  webhook,
  reloadWebhooks,
  onClose,
  onWebhookDelete: deleteWebhook,
}: Props): React.ReactElement => {
  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const toast = useToast()
  const { user } = useContext(UserContext)
  const { t } = useTranslation()
  const [formError, setFormError] = useState<string>('')

  const closeModal = (): void => {
    setFormError('')
    onClose()
  }

  const onSubmit = (): void => {
    const clientNumber = user?.clientNumber
    if (!clientNumber || !webhook?.id) {
      return
    }
    request
      .delete(
        `/api/clients/${clientNumber}/settings/developer/webhook/${webhook.id}`,
      )
      .then(() => {
        if (request.error) {
          switch (request.error.message) {
            case 'Not Found':
              reloadWebhooks() // This situation can happen if the webhook was deleted by another user.
              toast({
                description: t('webhook_not_found', 'Webhook not found'),
                status: 'warning',
                duration: 5000,
                isClosable: true,
              })
              break
            default:
              throw new Error(request.error.message)
          }
        } else {
          toast({
            description: t('webhook_deleted', 'Webhook deleted'),
            status: 'success',
            duration: 5000,
            isClosable: true,
          })
          deleteWebhook(webhook.id)
        }
        closeModal()
      })
      .catch(error => {
        setFormError(error.message)
      })
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <Formik
          initialValues={{
            endpointUrl: '',
          }}
          onSubmit={onSubmit}
        >
          {({ isSubmitting, submitForm }) => (
            <>
              <ModalHeader>{t('delete_webhook', 'Delete webhook')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody spacing={10}>
                {formError ? (
                  <Alert status="error" mb="1em" minWidth="unset">
                    <AlertIcon />
                    <AlertDescription>{formError}</AlertDescription>
                  </Alert>
                ) : null}
                <Text>
                  {t(
                    'delete_modal_paragraph',
                    'This action is irreversible. Are you sure you want to delete this webhook?',
                  )}
                </Text>
                <Text color={colors.appBattleshipGrey} padding={4}>{webhook?.endpoint}</Text>
              </ModalBody>
              <ModalFooter
                borderTop={`1px solid ${colors.appGrey}`}
                style={{ gap: '1em' }}
              >
                <Button
                  onClick={() => {
                    closeModal()
                    setFormError('')
                  }}
                  variant="outline"
                >
                  {t('cancel', 'Cancel')}
                </Button>
                <Button
                  isDisabled={isSubmitting}
                  isLoading={isSubmitting}
                  onClick={submitForm}
                  backgroundColor={colors.appRed}
                >
                  {t('delete', 'Delete')}
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default WebhookDeleteModal
